import { useMemo } from "react";
import { useLocation } from "react-router-dom";
import type {
  InteractionContext,
  PlayerAIScope,
} from "@playbookai/player-ai-client";
import {
  CURRENT_PAGE_CONTEXT_LIMITS,
  PLATFORM_ENTITY_TYPES,
  PLATFORM_MODULE_IDS,
  PLATFORM_PAGE_SURFACES,
  type CurrentPageContext,
  type CurrentPageFilter,
  type PlatformEntityRef,
  type PlatformModuleId,
} from "@playbookai/types";
import { useAuthStore } from "../auth/auth-store.js";
import { isModuleAvailable } from "../modules/index.js";
import { UUID_PATTERN } from "../modules/launcher-entries.js";

const SOLVE_ROUTE_PREFIX = "/app/solve/";
const HOSHIN_ROUTE_PREFIX = "/app/hoshin/";
const VSM_ROUTE_PREFIX = "/app/vsm/";
const PLATFORM_MODULE_SET: ReadonlySet<string> = new Set(PLATFORM_MODULE_IDS);
const PLATFORM_SURFACE_SET: ReadonlySet<string> = new Set(
  PLATFORM_PAGE_SURFACES,
);
const SAFE_FILTER_KEYS: ReadonlySet<string> = new Set([
  "assigned_to",
  "category",
  "due",
  "method",
  "mode",
  "owner",
  "owner_id",
  "phase",
  "q",
  "rag",
  "scope",
  "sort",
  "status",
  "tab",
  "view",
  "wave",
]);

function solveIdFromPath(path: string): string | null {
  if (!path.startsWith(SOLVE_ROUTE_PREFIX)) return null;
  const [instanceId, solveId] = path
    .slice(SOLVE_ROUTE_PREFIX.length)
    .split("/");
  if (!instanceId || !solveId || !UUID_PATTERN.test(solveId)) return null;
  return solveId;
}

function vsmIdFromPath(path: string): string | null {
  // /app/vsm/:instanceId/:vsmId — the instance segment is shell tab identity
  // only; the workspace id is the second segment (PB-427 route shape).
  if (!path.startsWith(VSM_ROUTE_PREFIX)) return null;
  const [instanceId, vsmId] = path.slice(VSM_ROUTE_PREFIX.length).split("/");
  if (!instanceId || !vsmId || !UUID_PATTERN.test(vsmId)) return null;
  return vsmId;
}

function hoshinIdFromPath(path: string): string | null {
  if (!path.startsWith(HOSHIN_ROUTE_PREFIX)) return null;
  const [planId] = path.slice(HOSHIN_ROUTE_PREFIX.length).split("/");
  if (!planId || !UUID_PATTERN.test(planId)) return null;
  return planId;
}

/**
 * Derives only the V2 conversation scope. Tenant and user identity are always
 * taken from the authenticated BFF session and never enter request bodies.
 */
export function useCurrentPlayerAIScope(): PlayerAIScope | null {
  const location = useLocation();
  const authenticated = useAuthStore(
    (state) => Boolean(state.claims?.userId) && Boolean(state.claims?.tenantId),
  );

  return useMemo<PlayerAIScope | null>(() => {
    if (!authenticated) return null;
    const solveId = solveIdFromPath(location.pathname);
    if (solveId) {
      return {
        conversationType: "workspace",
        workspaceType: "solve",
        workspaceId: solveId,
      };
    }
    const hoshinId = hoshinIdFromPath(location.pathname);
    if (hoshinId) {
      // Plan Edit and Monitor (`?mode=edit`, `?mode=monitor`, or no mode) use
      // owner-private contextual advisory threads; the legacy shared plan
      // conversation is readable history only. The workshop session surface
      // (`?mode=session`) keeps the workspace scope until the session page
      // publishes its own workshop override.
      const mode = new URLSearchParams(location.search).get("mode");
      if (mode === "session") {
        return {
          conversationType: "workspace",
          workspaceType: "hoshin",
          workspaceId: hoshinId,
        };
      }
      return {
        conversationType: "private",
        workspaceType: "hoshin",
        workspaceId: hoshinId,
      };
    }
    const vsmId = vsmIdFromPath(location.pathname);
    if (vsmId) {
      // The scope names the CONTEXTUAL-PRIVATE arm; it does not decide the
      // surface. The server resolves which conversation is active for this VSM
      // from its workshop execution state, and the client opens whatever it
      // answers — so a running workshop routes the same scope to the shared
      // conversation without the browser knowing the rule.
      return {
        conversationType: "private",
        workspaceType: "vsm",
        workspaceId: vsmId,
      };
    }
    // Every other authenticated shell route shares the root-private Today
    // conversation. A route can still publish a more precise module scope via
    // ShellBridge, but Settings/Admin/Notifications and module libraries never
    // lose the universal Player AI entry point.
    return { conversationType: "private" };
  }, [authenticated, location.pathname, location.search]);
}

/**
 * Minimal route-derived context. It contains navigation semantics only; page
 * text, canvas state, form drafts, files, identity, and authorization facts
 * are deliberately not observable here.
 */
export function useCurrentPlayerAIPageContext():
  | CurrentPageContext
  | undefined {
  const location = useLocation();
  const tenantEnabledModules = useAuthStore(
    (state) => state.claims?.enabledModules,
  );
  return useMemo(
    () =>
      currentPageContextFromLocation(
        location.pathname,
        location.search,
        (module) => isModuleAvailable(module, tenantEnabledModules),
      ),
    [location.pathname, location.search, tenantEnabledModules],
  );
}

/**
 * The page context, minus any module this workspace cannot use (ADR-0100): a
 * page of an off module — a KPI Manager KPI while KPI is off, say — tells
 * Player AI the surface it is on and nothing about the module's records.
 */
export function currentPageContextFromLocation(
  pathname: string,
  search = "",
  isAvailable: (module: PlatformModuleId) => boolean = () => true,
): CurrentPageContext | undefined {
  const context = pageContextFromLocation(pathname, search);
  const module = context?.module ?? context?.entity?.module;
  if (!context || module === undefined || isAvailable(module)) {
    return context;
  }
  const {
    module: _module,
    entity: _entity,
    selected_refs: _refs,
    ...rest
  } = context;
  return rest;
}

function pageContextFromLocation(
  pathname: string,
  search: string,
): CurrentPageContext | undefined {
  const params = new URLSearchParams(search);
  if (pathname === "/") return { surface: "home" };
  if (pathname === "/launcher" || pathname.startsWith("/launcher/")) {
    return { surface: "launcher" };
  }
  if (pathname === "/notifications") return { surface: "notifications" };
  if (pathname === "/player-ai") return { surface: "player_ai" };

  if (pathname === "/settings" || pathname.startsWith("/settings/")) {
    const segments = pathSegments(pathname, "/settings");
    const subsection = segments[0];
    const kpiId =
      subsection === "kpi" &&
      segments[1] !== undefined &&
      segments[1] !== "create"
        ? segments[1]
        : undefined;
    return compactContext({
      surface: "settings",
      ...(subsection ? { subsection } : {}),
      ...(kpiId
        ? {
            module: "kpi",
            entity: {
              module: "kpi",
              entity_type: "kpi",
              entity_id: kpiId,
              ...(segments[2] ? { subsection: segments[2] } : {}),
            },
          }
        : {}),
      filters: safeFilters(params),
    });
  }

  if (pathname === "/admin" || pathname.startsWith("/admin/")) {
    const [subsection] = pathSegments(pathname, "/admin");
    return compactContext({
      surface: "admin",
      ...(subsection ? { subsection } : {}),
      filters: safeFilters(params),
    });
  }

  const solveId = solveIdFromPath(pathname);
  if (pathname === "/app/solve" || pathname.startsWith(SOLVE_ROUTE_PREFIX)) {
    return compactContext({
      surface: "module",
      module: "solve",
      ...(solveId
        ? {
            entity: {
              module: "solve",
              entity_type: "solve",
              entity_id: solveId,
            },
          }
        : {}),
      subsection: solveId
        ? pathSegments(pathname, SOLVE_ROUTE_PREFIX)[2]
        : undefined,
      filters: safeFilters(params),
    });
  }

  const vsmId = vsmIdFromPath(pathname);
  if (pathname === "/app/vsm" || pathname.startsWith(VSM_ROUTE_PREFIX)) {
    const mode = params.get("mode");
    return compactContext({
      surface: "module",
      module: "vsm",
      ...(vsmId
        ? {
            entity: {
              module: "vsm",
              entity_type: "vsm",
              entity_id: vsmId,
            },
          }
        : {}),
      ...(mode === "edit" || mode === "monitor" ? { subsection: mode } : {}),
      filters: safeFilters(params),
    });
  }

  const hoshinId = hoshinIdFromPath(pathname);
  if (pathname === "/app/hoshin" || pathname.startsWith(HOSHIN_ROUTE_PREFIX)) {
    const segments = pathSegments(pathname, HOSHIN_ROUTE_PREFIX);
    const mode = params.get("mode");
    return compactContext({
      surface: "module",
      module: "hoshin",
      ...(hoshinId
        ? {
            entity: {
              module: "hoshin",
              entity_type: "hoshin_plan",
              entity_id: hoshinId,
            },
          }
        : {}),
      ...(segments[1] === "tti"
        ? { subsection: "x_matrix_item" }
        : mode === "edit" || mode === "monitor" || mode === "session"
          ? { subsection: mode }
          : {}),
      filters: safeFilters(params),
    });
  }

  if (pathname === "/app/lama" || pathname.startsWith("/app/lama/")) {
    const segments = pathSegments(pathname, "/app/lama/");
    // The standalone participant assessment (`/app/lama/assess?waveId=…`) is a
    // sibling of `:instanceId`; the wave id is its only entity fact. UUID-shape
    // checked because it arrives via query string rather than a typed route.
    if (segments[0] === "assess") {
      const waveId = params.get("waveId")?.trim();
      return compactContext({
        surface: "module",
        module: "lama",
        ...(waveId && UUID_PATTERN.test(waveId)
          ? {
              entity: {
                module: "lama",
                entity_type: "wave",
                entity_id: waveId,
              } as const,
            }
          : {}),
        subsection: "assess",
        filters: safeFilters(params),
      });
    }
    const [instanceId, collection, entityId, drill] = segments;
    const entity =
      instanceId && collection === "questionnaires" && entityId
        ? ({
            module: "lama",
            entity_type: "questionnaire",
            entity_id: entityId,
          } as const)
        : instanceId && collection === "campaigns" && entityId
          ? ({
              module: "lama",
              entity_type: "campaign",
              entity_id: entityId,
            } as const)
          : // A report is campaign-derived; the ref's entity_id IS the
            // campaign id in the route (`reports/:campaignId`).
            instanceId && collection === "reports" && entityId
            ? ({
                module: "lama",
                entity_type: "report",
                entity_id: entityId,
              } as const)
            : undefined;
    return compactContext({
      surface: "module",
      module: "lama",
      ...(entity ? { entity } : {}),
      // The response drill-down (`reports/:campaignId/responses`) names its
      // subsection so the run can tell the report page from its rows.
      ...(collection === "reports" && drill === "responses"
        ? { subsection: "responses" }
        : collection
          ? { subsection: collection }
          : {}),
      filters: safeFilters(params),
    });
  }

  if (pathname === "/app" || pathname.startsWith("/app/")) {
    // A mounted module outside the first-release capability matrix still gets
    // a surface hint, but no invented module id or entity reference.
    return { surface: "module" };
  }
  return undefined;
}

/** Runtime boundary for module-published context; rebuilds the closed shape. */
export function sanitizeCurrentPageContext(
  candidate: CurrentPageContext | undefined,
): CurrentPageContext | undefined {
  if (!candidate || !PLATFORM_SURFACE_SET.has(candidate.surface)) {
    return undefined;
  }
  const module = isPlatformModule(candidate.module)
    ? candidate.module
    : undefined;
  const entity = sanitizeEntityRef(candidate.entity);
  const subsection = boundedText(candidate.subsection);
  const filters = sanitizeFilters(candidate.filters);
  const selectedRefs = candidate.selected_refs
    ?.slice(0, CURRENT_PAGE_CONTEXT_LIMITS.selectedRefs)
    .map(sanitizeEntityRef)
    .filter((ref): ref is PlatformEntityRef => ref !== undefined);
  return compactContext({
    surface: candidate.surface,
    ...(module ? { module } : {}),
    ...(entity ? { entity } : {}),
    ...(subsection ? { subsection } : {}),
    ...(filters.length > 0 ? { filters } : {}),
    ...(selectedRefs && selectedRefs.length > 0
      ? { selected_refs: selectedRefs }
      : {}),
  });
}

export function mergeCurrentPageInteractionContext(
  source: InteractionContext | undefined,
  currentPageContext: CurrentPageContext | undefined,
): InteractionContext | undefined {
  if (!source && !currentPageContext) return undefined;
  const boundedSource = { ...(source ?? {}) };
  delete boundedSource.current_page_context;
  return {
    ...boundedSource,
    ...(currentPageContext ? { current_page_context: currentPageContext } : {}),
  };
}

function sanitizeEntityRef(
  candidate: PlatformEntityRef | undefined,
  depth = 0,
): PlatformEntityRef | undefined {
  if (!candidate || depth > 4 || !isPlatformModule(candidate.module)) {
    return undefined;
  }
  if (
    !PLATFORM_ENTITY_TYPES[candidate.module].includes(
      candidate.entity_type as never,
    )
  ) {
    return undefined;
  }
  const entityId = boundedText(candidate.entity_id);
  if (!entityId) return undefined;
  const parent = sanitizeEntityRef(candidate.parent, depth + 1);
  const subsection = boundedText(candidate.subsection);
  return {
    module: candidate.module,
    entity_type: candidate.entity_type,
    entity_id: entityId,
    ...(parent ? { parent } : {}),
    ...(subsection ? { subsection } : {}),
  } as PlatformEntityRef;
}

function compactContext(context: CurrentPageContext): CurrentPageContext {
  return sanitizeUndefined(context) as CurrentPageContext;
}

function sanitizeUndefined(value: object): object {
  return Object.fromEntries(
    Object.entries(value).filter(([, entry]) => entry !== undefined),
  );
}

function pathSegments(pathname: string, prefix: string): string[] {
  const suffix = pathname.startsWith(prefix)
    ? pathname.slice(prefix.length)
    : "";
  return suffix
    .split("/")
    .filter(Boolean)
    .map((segment) => decodeURIComponentSafely(segment));
}

function decodeURIComponentSafely(value: string): string {
  try {
    return decodeURIComponent(value);
  } catch {
    return value;
  }
}

function safeFilters(
  params: URLSearchParams,
): readonly CurrentPageFilter[] | undefined {
  const filters = sanitizeFilters(
    [...SAFE_FILTER_KEYS]
      .filter((key) => params.has(key))
      .map((key) => ({ key, values: params.getAll(key) })),
  );
  return filters.length > 0 ? filters : undefined;
}

function sanitizeFilters(
  filters: readonly CurrentPageFilter[] | undefined,
): CurrentPageFilter[] {
  if (!filters) return [];
  const sanitized: CurrentPageFilter[] = [];
  for (const filter of filters.slice(0, CURRENT_PAGE_CONTEXT_LIMITS.filters)) {
    if (!SAFE_FILTER_KEYS.has(filter.key)) continue;
    const values = filter.values
      .slice(0, CURRENT_PAGE_CONTEXT_LIMITS.filterValues)
      .map(boundedText)
      .filter((value): value is string => Boolean(value));
    if (values.length > 0) sanitized.push({ key: filter.key, values });
  }
  return sanitized;
}

function boundedText(value: string | undefined): string | undefined {
  const normalized = value?.trim();
  if (!normalized) return undefined;
  return normalized.slice(0, CURRENT_PAGE_CONTEXT_LIMITS.textLength);
}

function isPlatformModule(
  value: string | undefined,
): value is PlatformModuleId {
  return value !== undefined && PLATFORM_MODULE_SET.has(value);
}

export function useCurrentPlayerAIContext():
  | { label: string; sublabel?: string }
  | undefined {
  const location = useLocation();
  return useMemo(() => {
    const path = location.pathname;
    if (path === "/") return { label: "Home" };
    if (path === "/launcher" || path.startsWith("/launcher/")) {
      return { label: "Launcher" };
    }
    if (path === "/notifications") return { label: "Notifications" };
    if (path === "/settings" || path.startsWith("/settings/")) {
      const [section] = pathSegments(path, "/settings");
      return section
        ? { label: "Settings", sublabel: section }
        : { label: "Settings" };
    }
    if (path === "/admin" || path.startsWith("/admin/")) {
      const [section] = pathSegments(path, "/admin");
      return section
        ? { label: "Admin", sublabel: section }
        : { label: "Admin" };
    }
    const solveId = solveIdFromPath(path);
    if (solveId) return { label: "SOLVE", sublabel: solveId };
    if (path.startsWith(SOLVE_ROUTE_PREFIX)) return { label: "SOLVE" };
    const hoshinId = hoshinIdFromPath(path);
    if (hoshinId) return { label: "Hoshin", sublabel: hoshinId };
    if (path === "/app/hoshin" || path.startsWith(HOSHIN_ROUTE_PREFIX)) {
      return { label: "Hoshin" };
    }
    const vsmId = vsmIdFromPath(path);
    if (vsmId) return { label: "VSM", sublabel: vsmId };
    if (path === "/app/vsm" || path.startsWith(VSM_ROUTE_PREFIX)) {
      return { label: "VSM" };
    }
    return undefined;
  }, [location.pathname]);
}
